import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Users, Clock, Star, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useLang } from '../contexts/LanguageContext';
import { getLocalizedField } from '../utils/localizedContent';


export default function RecommendedForYou() {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { addItem } = useCart();
  const { lang, t } = useLang();

  const fetchRecommendations = useCallback(async () => {
    setLoading(true);
    try {
      // Reservas pasadas del usuario para inferir preferencias
      let preferredPlayerCount = 4; // Valor por defecto
      let pastGameIds = [];
      
      if (user) {
        try {
          const { data: reservations } = await axios.get(`${API}/reservations/my`);
          if (reservations && reservations.length > 0) {
            // Tamaño medio de grupo desde reservas
            const partySizes = reservations
              .filter(r => r.party_size)
              .map(r => r.party_size);
            if (partySizes.length > 0) {
              preferredPlayerCount = Math.round(partySizes.reduce((a, b) => a + b, 0) / partySizes.length);
            }
            // Juegos que ya reservó
            pastGameIds = reservations.map(r => r.game_id).filter(Boolean);
          }
        } catch {}
      }

      // Cargar productos y filtrar recomendaciones
      const { data } = await axios.get(`${API}/products`, { params: { limit: 50 } });
      const products = data.products || [];
      
      // Puntuar productos según:
      // 1. Coincidencia de número de jugadores
      // 2. Valoración
      // 3. No ya reservado
      // 4. Priorizar juegos alquilables
      const scored = products.map(p => {
        let score = 0;
        
        // Coincidencia de jugadores (±1 respecto al preferido)
        if (p.min_players && p.max_players) {
          if (preferredPlayerCount >= p.min_players && preferredPlayerCount <= p.max_players) {
            score += 30; // Coincidencia perfecta
          } else if (preferredPlayerCount >= p.min_players - 1 && preferredPlayerCount <= p.max_players + 1) {
            score += 15; // Coincidencia cercana
          }
        }
        
        // Bonus por valoración
        score += (p.rating || 0) * 5;
        
        // Bonus por alquilable
        if (p.is_rentable) score += 10;
        
        // Penalización si ya lo reservó
        if (pastGameIds.includes(p.product_id)) score -= 20;
        
        // Diversidad de categoría: ligero impulso a TCG
        if (p.category === 'tcg') score += 5;
        
        return { ...p, score };
      });

      // Ordenar por puntuación y quedarse con los 6 mejores
      const recommended = scored
        .sort((a, b) => b.score - a.score)
        .slice(0, 6);
      
      setRecommendations(recommended);
    } catch (error) {
      // Respaldo: mostrar solo productos populares
      try {
        const { data } = await axios.get(`${API}/products`, { params: { limit: 6, sort: 'rating' } });
        setRecommendations(data.products || []);
      } catch {}
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-6">
          <Sparkles size={20} className="text-[#C9A962]" />
          <h2 className="text-2xl font-bold text-[#F5F0E6]" style={{ fontFamily: 'Cinzel, serif' }}>
            {t('recommendations.title')}
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={`rec-skeleton-${i}`} className="card-glass overflow-hidden">
              <div className="aspect-square skeleton"></div>
              <div className="p-3 space-y-2">
                <div className="h-4 skeleton rounded"></div>
                <div className="h-3 w-2/3 skeleton rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (recommendations.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 py-8" data-testid="recommended-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#C9A962]/20 flex items-center justify-center">
            <Sparkles size={16} className="text-[#C9A962]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#F5F0E6]" style={{ fontFamily: 'Cinzel, serif' }}>
              {t('recommendations.title')}
            </h2>
            <p className="text-xs text-[#A8B5AE]">
              {user 
                ? t('recommendations.based_on_reservations')
                : t('recommendations.most_popular')}
            </p>
          </div>
        </div>
        <Link to="/store" className="flex items-center gap-1 text-[#C9A962] text-sm font-medium hover:gap-2 transition-all">
          {t('common.view_all')} <ChevronRight size={16} />
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {recommendations.map(product => (
          <div key={product.product_id} className="card-glass overflow-hidden group" data-testid={`recommended-${product.product_id}`}>
            <Link to={`/store/${product.product_id}`}>
              <div className="aspect-square overflow-hidden relative">
                <img 
                  src={product.images?.[0] || 'https://images.unsplash.com/photo-1611371805429-8b5c1b2c34ba?w=300&h=300&fit=crop'} 
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                />
                {product.is_rentable && (
                  <span className="absolute top-2 left-2 bg-[#C9A962]/90 text-[#0A1612] text-xs px-2 py-0.5 rounded-full font-semibold">
                    {t('recommendations.rent')}
                  </span>
                )}
              </div>
              <div className="p-3">
                <p className="text-[#F5F0E6] text-xs font-semibold truncate">
                  {getLocalizedField(product, 'name', lang, product.name)}
                </p>
                <div className="flex items-center gap-2 mt-1 text-xs text-[#A8B5AE]">
                  {product.min_players && (
                    <span className="flex items-center gap-0.5">
                      <Users size={10} />
                      {product.min_players}-{product.max_players}
                    </span>
                  )}
                  {product.duration_minutes && (
                    <span className="flex items-center gap-0.5">
                      <Clock size={10} />
                      {product.duration_minutes}m
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-1">
                    <Star size={10} className="text-[#C9A962] fill-[#C9A962]" />
                    <span className="text-[#F5F0E6] text-xs font-medium">{product.rating?.toFixed(1) || '—'}</span>
                  </div>
                  <span className="text-[#C9A962] text-sm font-bold">{product.price?.toFixed(2)}€</span>
                </div>
              </div>
            </Link>
            <div className="px-3 pb-3">
              <button 
                onClick={(e) => { e.preventDefault(); addItem(product); }}
                className="w-full text-xs font-semibold py-1.5 rounded-lg bg-[#C9A962]/10 text-[#C9A962] hover:bg-[#C9A962]/20 transition-colors"
                data-testid={`add-recommended-${product.product_id}`}
              >
                + {t('recommendations.add')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
